// ============================================================================
// quote-corpus-rebuild.js — turn a corpus entry back into a working project.
// ----------------------------------------------------------------------------
// "Rebuild this quote": the operator picks a historical quote in the Quote
// Corpus panel and gets a NEW project with Stage 1 pre-filled (address,
// client, strata plan, notes) and Stage 4 pre-seeded with the quote's scope,
// parsed from the VERBATIM sections. Running the normal pipeline then
// regenerates the quote in the operator's voice — with the original entry
// retrievable as its own top exemplar when qm_use_quote_corpus_rag is on.
//
// Parsing is deliberately conservative: a numbered multi-item quote
// ("1.  Granny Flat" ... "2.  Sliding Door") becomes one scope item per
// numbered block; anything else becomes ONE scope item whose inclusions are
// the quote's bullet lines. Bullets are stripped of their glyphs here because
// scope-item inclusions are STRUCTURED fields (the verbatim exhibit remains
// the corpus entry itself, linked via corpusSource).
// ============================================================================

import { genItemId } from '../utils/item-id';

const NUMBERED_HEAD = /^(\d{1,2})\.\s+(.{3,80})$/;

function sectionMap(entry) {
    const out = new Map();
    for (const s of (Array.isArray(entry.sections) ? entry.sections : [])) {
        const name = String(s?.h || '').toLowerCase();
        if (!out.has(name)) out.set(name, String(s?.t || ''));
    }
    return out;
}

function cleanLine(ln) {
    return ln.replace(/^[\s·•Øo*\-\t]+/, '').trim();
}

/** Split the Details text into scope items. */
export function parseScopeFromDetails(details, fallbackTitle, price) {
    const lines = String(details || '').split('\n');
    const blocks = [];
    let current = null;

    for (const raw of lines) {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        const numbered = trimmed.match(NUMBERED_HEAD);
        if (numbered) {
            if (current) blocks.push(current);
            current = { title: numbered[2].trim(), lines: [] };
            continue;
        }
        const cleaned = cleanLine(trimmed);
        if (!cleaned || /^the following items are included/i.test(cleaned)) continue;
        if (/^total price$/i.test(cleaned) || /^including gst/i.test(cleaned) || /^\(valid for/i.test(cleaned)) continue;
        if (/^\$[\d,. ]+$/.test(cleaned)) continue; // bare amount line under a Total Price heading
        if (current) current.lines.push(cleaned);
        else (current = { title: null, lines: [cleaned] });
    }
    if (current) blocks.push(current);

    const named = blocks.filter((b) => b.title);
    if (named.length >= 2) {
        return named.map((b, i) => ({
            id: genItemId(),
            code: String(i + 1),
            title: b.title.slice(0, 120),
            descriptionBrief: '',
            description: b.lines[0] ? b.lines[0].slice(0, 300) : '',
            inclusions: b.lines.slice(0, 25),
            amount: '0.00',
            images: [],
        }));
    }

    const allLines = blocks.flatMap((b) => (b.title ? [b.title, ...b.lines] : b.lines));
    if (allLines.length === 0) return [];
    return [{
        id: genItemId(),
        code: '1',
        title: (fallbackTitle || 'Rebuilt scope').slice(0, 120),
        descriptionBrief: '',
        description: allLines[0] ? allLines[0].slice(0, 300) : '',
        inclusions: allLines.slice(0, 30),
        amount: price != null && Number.isFinite(Number(price)) ? Number(price).toFixed(2) : '0.00',
        images: [],
    }];
}

/**
 * Build the Stage 1 form patch + Stage 4 scope from a corpus entry
 * (as returned by GET /api/quote-corpus/:id — camelCased).
 */
export function buildDraftFromCorpusEntry(entry) {
    const secs = sectionMap(entry);
    const objective = (secs.get('objective') || '').trim();
    const details = secs.get('details') || secs.get('scope of works') || '';

    const provenance = `Rebuilt from historical quote ${entry.quoteRef || entry.sourceFile || entry.id} (corpus entry, ${String(entry.quoteDate || '').slice(0, 10) || 'undated'}).`;
    const notes = [objective, '', provenance].filter(Boolean).join('\n');

    const addressLine = (entry.siteAddress || '').split(',').slice(0, 2).join(',').trim();

    return {
        formData: {
            address: addressLine || (entry.suburb || ''),
            strataplan: entry.strataPlan || '',
            clientName: entry.clientName || '',
            notes,
        },
        scope: parseScopeFromDetails(details, entry.summary, entry.price),
        corpusSource: {
            entryId: entry.id,
            quoteRef: entry.quoteRef || null,
            sourceFile: entry.sourceFile || null,
            originalPrice: entry.price ?? null,
        },
    };
}
